import { showDeleteConfirmation } from '@modules/common';
import { map, switchMap } from 'rxjs/operators';
import { BaseBloc } from '@core/utils/bloc';
import { Repository } from '@core/utils/repository';
import { handleMessage, requestMessage } from '@business/messages';
import { ChecklistTemplateId, ChecklistTemplateResponse, CompactChecklistTemplateResponse, ChecklistId } from '@business/entities/checklist';
import { R } from '@core/utils/r';
import { TodoChecklistAPI } from '@business/api/TodoChecklist';
import { TeamId } from '@business/entities/team';
import { UserId } from '@business/entities/account';

export class ChildChecklistBloc extends BaseBloc {
  private readonly checklistRepo = new Repository<CompactChecklistTemplateResponse | ChecklistTemplateResponse>({
    getItemId: checklist => checklist.id,
  });

  readonly checklists$ = this.checklistRepo.items$;
  readonly selectChecklist = this.checklistRepo.selectItem;

  readonly selectStep = (checklistId: ChecklistId, stepId: number) =>
    this.checklistRepo.selectItem(checklistId).pipe(
      map(checklist => {
        if (!checklist) return undefined;
        let allSteps = R.flatMap((checklist as ChecklistTemplateResponse).sections || [], section => section.steps);
        allSteps = R.merge(allSteps, (checklist as ChecklistTemplateResponse).steps);
        return allSteps.find(s => s.id === stepId);
      }),
    );

  onReset() {
    this.checklistRepo.reset();
  }

  fetchChecklists = (parentId?: ChecklistId) => {
    return TodoChecklistAPI.fetchTodoChecklists(parentId).pipe(
      this.checklistRepo.ops.reset(),
      handleMessage({ error: requestMessage('fetch_templates_error') }),
    );
  };

  fetchChecklist = (id: ChecklistTemplateId) => {
    return TodoChecklistAPI.fetchTodoChecklist(id).pipe(
      this.checklistRepo.ops.upsertOne(item => ({ item })),
      handleMessage({ error: requestMessage('fetch_templates_error') }),
    );
  };

  deleteChecklist = (id: ChecklistTemplateId) => {
    return showDeleteConfirmation('Delete Checklist', 'Do you really want to remove this checklist?').pipe(
      switchMap(() => TodoChecklistAPI.deleteTodoChecklist(id)),
      this.checklistRepo.ops.removeOne(() => id),
      handleMessage({
        type: requestMessage('delete_checklist'),
      }),
    );
  };

  removeChecklistAssignees = (id: ChecklistTemplateId) => {
    return showDeleteConfirmation('Delete Checklist', 'Do you really want to remove this checklist?').pipe(
      switchMap(() => TodoChecklistAPI.removeChecklistAssignees(id)),
      handleMessage({
        type: requestMessage('delete_checklist'),
      }),
    );
  };

  search = (id: UserId | TeamId) => {
    return TodoChecklistAPI.search(id).pipe(this.checklistRepo.ops.reset(), handleMessage({ error: requestMessage('fetch_templates_error') }));
  };
}
